import { createSlice } from "@reduxjs/toolkit";
import {
  MeasureDescriptor,
  MeasureUsage,
  MetaItemType,
  MetricDescriptor,
  ReportType,
} from "../../../shared/reporting/api/biClient.types";
import cloneDeep from "../../../shared/utilities/cloneDeep";
import { MeasuresStructure } from "../../api/biApi.types";
import { updateDataSet } from "../../api/dataSetApiContext";
import { DataSetMetaDataState, MetaDataActionTypes, MetaDataState } from "../types/metaDataState.types";
import { META_DATA_SLICE_NAME } from "../utils/storeUtils";

const dataSetMetaDataEmpty: DataSetMetaDataState = {
  dataSetId: "undefined",
  dimensionsStructure: {
    dimensions: [],
    groups: [],
  },
  measuresStructure: {
    names: [],
    groups: [],
  },
  dimensions: [],
  measures: [],
  allMeasures: [],
  allMetrics: [],
  functions: [],
  measureGroups: [],
  metrics: [],
  metricsStructure: {
    names: [],
    groups: [],
  },
};

const initialState: MetaDataState = {
  dataSets: [],
  loaded: false,
  dataSetId: undefined,
};

const addToGroup = (measuresStructure: MeasuresStructure, measure: MeasureDescriptor) => {
  const group = measuresStructure.groups.find((g) => g.caption === measure.group);
  if (group !== undefined) {
    group.names.push(measure.name);
  }
};

const removeFromGroup = (measuresStructure: MeasuresStructure, measure: MeasureDescriptor, groupName: string) => {
  const group = measuresStructure.groups.find((g) => g.caption === groupName);
  if (group !== undefined) {
    const index = group.names.indexOf(measure.name);
    if (index > -1) {
      group.names.splice(index, 1);
    }
  }
};

export const metaDataSlice = createSlice({
  name: META_DATA_SLICE_NAME,
  initialState,
  reducers: {
    update: (state, action: MetaDataActionTypes["update"]) => {
      Object.assign(state, action.payload);
      filterMeasures(state);
    },
    addMeasure: (state, action: MetaDataActionTypes["addMeasure"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined) {
        return;
      }

      const measure = action.payload;
      measure.itemType = MetaItemType.MEASURE;
      dataSet.allMeasures.push(measure);
      dataSet.measures.push(measure);
      addToGroup(dataSet.measuresStructure, measure);
    },
    updateMeasure: (state, action: MetaDataActionTypes["updateMeasure"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined) {
        return;
      }
      const stateMeasure = dataSet.measures.find((m) => m.name === action.payload.name);
      if (stateMeasure !== undefined) {
        const currentGroup = stateMeasure.group;
        const isGroupChanged = currentGroup !== action.payload.group;
        const updatedMeasure = Object.assign(stateMeasure, action.payload);

        if (isGroupChanged) {
          removeFromGroup(dataSet.measuresStructure, stateMeasure, currentGroup);
          addToGroup(dataSet.measuresStructure, updatedMeasure);
        }
      }
    },
    updateReportType: (state, action: MetaDataActionTypes["updateReportType"]) => {
      state.reportType = action.payload;
      filterMeasures(state);
    },
    deleteMeasure: (state, action: MetaDataActionTypes["deleteMeasure"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined) {
        return;
      }
      const measure = action.payload;
      const index = dataSet.allMeasures.findIndex((m) => m.name === measure.name);
      if (index < 0) return;
      dataSet.allMeasures.splice(index, 1);
      removeFromGroup(dataSet.measuresStructure, measure, measure.group);
      filterMeasures(state);
    },
    updateDictionaries: (state, action: MetaDataActionTypes["updateDictionaries"]) => {
      const dataSet = state.dataSets.find((d) => d.dataSetId === state.dataSetId);
      if (dataSet === undefined) {
        return;
      }
      Object.entries(action.payload).forEach(([key, value]) => {
        const dimension = dataSet.dimensions.find((d) => d.name === key);
        if (dimension !== undefined) {
          dimension.dictionary = cloneDeep(Object.values(value).map((v) => v));
        }
      });
    },
    setChartOfAccounts: (state, action: MetaDataActionTypes["setChartOfAccounts"]) => {
      state.chartOfAccounts = action.payload;
    },
    changeDataSet: (state, action: MetaDataActionTypes["changeDataSet"]) => {
      updateDataSetState(state, action.payload);
    },
  },
});

function filterMeasures(state: MetaDataState) {
  for (const dataSet of state.dataSets) {
    if (state.reportType === ReportType.Pivot) {
      dataSet.measures = filterMeasuresByUsage(dataSet.allMeasures, [MeasureUsage.PivotOnly]);
      dataSet.metrics = filterMeasuresByUsage(dataSet.allMetrics, [MeasureUsage.PivotOnly]);
    } else if (state.reportType === ReportType.Tabular) {
      dataSet.measures = filterMeasuresByUsage(dataSet.allMeasures, [MeasureUsage.TabularOnly]);
      dataSet.metrics = filterMeasuresByUsage(dataSet.allMetrics, [MeasureUsage.TabularOnly]);
    } else {
      dataSet.measures = dataSet.allMeasures;
      dataSet.metrics = dataSet.allMetrics;
    }
  }
}
const updateDataSetState = (state: MetaDataState, dataSetId: string) => {
  state.dataSetId = dataSetId;
  // Update the data set in the API context to ensure it is used in the API calls
  updateDataSet(dataSetId || "");
};

function filterMeasuresByUsage<T extends MeasureDescriptor | MetricDescriptor>(
  fields: T[],
  expectedUsages: MeasureUsage[]
) {
  return fields.filter(
    (field): field is T =>
      field.usage === undefined || field.usage === MeasureUsage.General || expectedUsages.includes(field.usage)
  );
}

export { dataSetMetaDataEmpty };

export default metaDataSlice.reducer;
